import { computed, inject, Injectable, signal } from '@angular/core';
import { KycReviewStage } from '../../onboarding/models/onboarding-models';
import { KycReviewStatusService } from '../../onboarding/services/kyc-review-status.service';
import { OnboardingStatusService } from '../../onboarding/services/onboarding-status.service';
import { KycInAppNotification, KycNotificationEventCode } from '../models/kyc-notification.models';

const READ_STATE_STORAGE_KEY = 'ccfx.ws.notifications.kyc.read';

/** Notification content per stage (FR-7.1–7.5 "In-App" copy), independent of admin/non-admin. */
const STAGE_NOTIFICATION: Partial<
  Record<Exclude<KycReviewStage, null>, Omit<KycInAppNotification, 'id' | 'stage'>>
> = {
  upcoming: {
    eventCode: 'KYC_REVIEW_UPCOMING',
    icon: '🔔',
    titleKey: 'notifications.kyc.upcoming.title',
    textKey: 'notifications.kyc.upcoming.text',
    buttonTextKey: 'notifications.kyc.upcoming.button',
    severity: 'info',
  },
  due: {
    eventCode: 'KYC_REVIEW_DUE',
    icon: '⚠️',
    titleKey: 'notifications.kyc.due.title',
    textKey: 'notifications.kyc.due.text',
    buttonTextKey: 'notifications.kyc.due.button',
    severity: 'warning',
  },
  warning: {
    eventCode: 'KYC_WARNING',
    icon: '⏰',
    titleKey: 'notifications.kyc.warning.title',
    textKey: 'notifications.kyc.warning.text',
    buttonTextKey: 'notifications.kyc.warning.button',
    severity: 'warning',
  },
  pastdue: {
    eventCode: 'KYC_PASTDUE',
    icon: '🚫',
    titleKey: 'notifications.kyc.pastdue.title',
    textKey: 'notifications.kyc.pastdue.text',
    buttonTextKey: 'notifications.kyc.pastdue.button',
    severity: 'urgent',
  },
  changeRequired: {
    eventCode: 'KYC_CHANGE_REQUIRED',
    icon: '📋',
    titleKey: 'notifications.kyc.changeRequired.title',
    textKey: 'notifications.kyc.changeRequired.text',
    buttonTextKey: 'notifications.kyc.changeRequired.button',
    severity: 'urgent',
  },
};

/** FR-7.4 "Past Due — Non Admin User" variant: no button, contact-admin copy only. */
const PASTDUE_NON_ADMIN_NOTIFICATION: Omit<KycInAppNotification, 'id' | 'stage'> = {
  eventCode: 'KYC_PASTDUE',
  icon: '🚫',
  titleKey: 'notifications.kyc.pastdueNonAdmin.title',
  textKey: 'notifications.kyc.pastdueNonAdmin.text',
  buttonTextKey: null,
  severity: 'urgent',
};

interface ReadState {
  [notificationId: string]: true;
}

function loadReadState(): ReadState {
  try {
    const raw = localStorage.getItem(READ_STATE_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ReadState) : {};
  } catch {
    return {};
  }
}

/**
 * Derives the FR-7 in-app notification (bell) list from the current KYC re-review stage.
 * There is one active notification at a time — historical/past notifications aren't tracked
 * on the frontend since no backend notification log is exposed to the customer portal yet.
 */
@Injectable({ providedIn: 'root' })
export class KycNotificationService {
  private readonly kycReviewStatus = inject(KycReviewStatusService);
  private readonly onboardingStatus = inject(OnboardingStatusService);

  private readonly _readState = signal<ReadState>(loadReadState());

  /** The single active notification for the current stage, or an empty array when none applies. */
  readonly notifications = computed<KycInAppNotification[]>(() => {
    const stage = this.kycReviewStatus.stage();
    if (!stage) return [];

    const isNonAdminPastDue = stage === 'pastdue' && !this.onboardingStatus.canManageKycReview();
    const content = isNonAdminPastDue ? PASTDUE_NON_ADMIN_NOTIFICATION : STAGE_NOTIFICATION[stage];
    if (!content) return [];

    const id = this.buildId(content.eventCode, isNonAdminPastDue);
    return [{ id, stage, ...content }];
  });

  readonly unreadCount = computed(
    () => this.notifications().filter((n) => !this._readState()[n.id]).length,
  );

  markAsRead(id: string): void {
    if (this._readState()[id]) return;
    this._readState.update((state) => ({ ...state, [id]: true }));
    this.persist();
  }

  markAllAsRead(): void {
    const ids = this.notifications().map((n) => n.id);
    if (ids.length === 0) return;
    this._readState.update((state) => {
      const next = { ...state };
      for (const id of ids) next[id] = true;
      return next;
    });
    this.persist();
  }

  private buildId(eventCode: KycNotificationEventCode, isNonAdminPastDue: boolean): string {
    const date = this.kycReviewStatus.kycReReviewDate() ?? 'unknown';
    const companyId = this.onboardingStatus.companyId() ?? 'unknown';
    return `${companyId}:${date}:${eventCode}${isNonAdminPastDue ? ':nonadmin' : ''}`;
  }

  private persist(): void {
    try {
      localStorage.setItem(READ_STATE_STORAGE_KEY, JSON.stringify(this._readState()));
    } catch {
      // localStorage unavailable (e.g. private browsing quota) — read state just won't persist.
    }
  }
}
